import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Radio, Upload, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import { scadaTagApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function triggerDownload(content, filename) {
  if (!content) return;
  let blob;
  if (filename?.endsWith('.xlsx')) {
    const bytes = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  } else {
    blob = new Blob([content], { type: 'text/plain' });
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// name/format/rows for each of the up-to-3 auto-downloaded files, keyed off the generate result.
// Row counts line up with the per-system summary shown in the result panel.
function describeOutputs(result, system) {
  const fmt = (name) => (name?.endsWith('.xlsx') ? 'excel' : name?.endsWith('.csv') ? 'csv' : 'txt');
  const out = [
    { content: result.fileA, filename: result.fileNameA, rows: system === 'CIM' ? result.hmiTagCount : result.ignFirstPassCount },
    { content: result.fileB, filename: result.fileNameB, rows: system === 'CIM' ? result.dataLogCount : result.hmiTagCount },
  ];
  if (result.fileC) out.push({ content: result.fileC, filename: result.fileNameC, rows: result.alarmConfigCount });
  return out.map(o => ({ ...o, format: fmt(o.filename) }));
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ScadaTagGenerationPage() {
  const { dbSlug: slug } = useDatabase();
  const { trackTransfer } = useDataTransfer();

  const [system, setSystem] = useState('CIM');
  const [version, setVersion] = useState('');
  const [versions, setVersions] = useState([]);
  const [plcName, setPlcName] = useState('');

  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [generateError, setGenerateError] = useState('');

  // Load versions when system changes
  useEffect(() => {
    scadaTagApi.getVersions(system)
      .then(res => {
        const vList = res.data?.versions || [];
        setVersions(vList);
        setVersion(vList[0] || '');
      })
      .catch(() => {
        setVersions([]);
        setVersion('');
      });
  }, [system]);

  const handleFile = useCallback((f) => {
    if (!f) return;
    setFile(f);
    setFileName(f.name);
    setGenerateResult(null);
    setGenerateError('');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleGenerate = async () => {
    if (!file) { setGenerateError('Upload a CSV file first.'); return; }
    if (!plcName.trim()) { setGenerateError('PLC Name is required.'); return; }
    if (!version) { setGenerateError('Select a version.'); return; }

    setGenerating(true);
    setGenerateError('');
    setGenerateResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('system', system);
    formData.append('version', version);
    formData.append('plcName', plcName.trim());

    try {
      const res = await scadaTagApi.generate(slug, formData);
      setGenerateResult(res.data);
      for (const out of describeOutputs(res.data, system)) {
        await trackTransfer({ name: out.filename, format: out.format, rows: out.rows }, async () => {
          triggerDownload(out.content, out.filename);
        });
      }
    } catch (err) {
      setGenerateError(err.response?.data || err.message || 'Generation failed.');
    } finally {
      setGenerating(false);
    }
  };

  const fileCount = 3;
  const fileLabels = system === 'CIM'
    ? ['CIM Import CSV (140 cols)', 'DataLog CSV', 'AlarmConfig XLSX']
    : ['IGN First Pass CSV', 'IGN Second Pass CSV', 'AlarmConfig XLSX'];

  return (
    <div className="dev-module import-template-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <Radio size={20} className="stui-imgupload-title-icon" />
            SCADA Tag Generation
          </h1>
          <span className="dev-module-meta">Tool 2 CSV &rarr; CIM Import / IGN Tag Files</span>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="import-steps-layout" style={{ maxWidth: '1100px', alignItems: 'flex-start' }}>

          {/* ═══════════ TILE 1: CONFIGURE + UPLOAD ═══════════ */}
          <div className="stui-step-panel" style={{ maxWidth: '420px' }}>
            <div className="stui-step-header">
              <span className="stui-step-number">1</span>
              <h3>SCADA System Configuration</h3>
            </div>

            {/* System toggle */}
            <div className="stui-field-group stui-mb-md">
              <label>SCADA System</label>
              <div className="stui-segmented">
                {['CIM', 'IGN'].map(s => (
                  <button
                    key={s}
                    onClick={() => { setSystem(s); setGenerateResult(null); setGenerateError(''); }}
                    className={`stui-segmented-btn${system === s ? ' selected' : ''}`}
                  >
                    {s === 'CIM' ? 'Cimplicity' : 'Ignition'}
                  </button>
                ))}
              </div>
            </div>

            {/* Version */}
            <div className="stui-field-group stui-mb-md">
              <label>Version</label>
              <StuiSelect
                value={version}
                onChange={e => setVersion(e.target.value)}
                className="stui-field-input"
              >
                {versions.map(v => <option key={v} value={v}>{v}</option>)}
              </StuiSelect>
            </div>

            {/* PLC Name */}
            <div className="stui-field-group stui-mb-md">
              <label>PLC / Device Name</label>
              <input
                type="text"
                value={plcName}
                onChange={e => setPlcName(e.target.value)}
                placeholder="e.g. FC1_PLC"
                className="stui-field-input"
              />
            </div>

            {/* File upload */}
            <div className="stui-field-group stui-mb-md">
              <label>Tool 2 Output CSV</label>
              <div
                className={`import-dropzone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="stui-hidden"
                  onChange={e => handleFile(e.target.files[0])}
                />
                <Upload size={28} />
                <span>{fileName || 'Drop .csv file here or click to browse'}</span>
              </div>
            </div>

            {/* Instructions */}
            <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', lineHeight: 1.6 }}>
              Upload the RSLogix 5000 CSV processed through Tool 2 (UDT Comment Generation).
              All COMMENT rows tagged <code>&lt;HMI&gt;</code> will be included in the output.
              TagXRef entries must be committed to the database before generating.
            </p>
          </div>

          {/* ═══════════ TILE 2: GENERATE ═══════════ */}
          <div className="stui-step-panel" style={{ maxWidth: '360px' }}>
            <div className="stui-step-header">
              <span className="stui-step-number">2</span>
              <h3>Generate</h3>
            </div>

            {/* Outputs description */}
            <div className="stui-inline-notice stui-mb-md" style={{ lineHeight: 1.6 }}>
              <strong>Outputs ({fileCount} files):</strong>
              <ul style={{ margin: 'var(--spacing-xs) 0 0', paddingLeft: 'var(--spacing-lg)' }}>
                {fileLabels.map(l => <li key={l}>{l}</li>)}
              </ul>
            </div>

            <button
              className="stui-btn stui-btn-primary stui-w-full stui-mb-md"
              onClick={handleGenerate}
              disabled={generating || !file}
            >
              {generating
                ? <><Loader2 size={16} className="stui-spinning stui-mr-sm" />Generating...</>
                : <><Radio size={16} className="stui-mr-sm" />Generate SCADA Tags</>
              }
            </button>

            {generateError && (
              <div className="import-val-result fail">
                <div className="import-val-summary">
                  <AlertTriangle size={14} />
                  <span>{generateError}</span>
                </div>
              </div>
            )}

            {generateResult && (
              <div className="import-val-result pass">
                <div className="import-val-summary">
                  <CheckCircle size={18} />
                  <span>Complete &mdash; {fileCount} files downloading</span>
                </div>
                <div className="import-val-section">
                  <div className="stui-text-xs" style={{ lineHeight: 1.8 }}>
                    {system === 'CIM' ? (
                      <>
                        <div>HMI tags: <strong>{generateResult.hmiTagCount}</strong></div>
                        <div>DataLog rows: <strong>{generateResult.dataLogCount}</strong></div>
                        <div>Alarm Config tags: <strong>{generateResult.alarmConfigCount}</strong></div>
                      </>
                    ) : (
                      <>
                        <div>First Pass Root Tags: <strong>{generateResult.ignFirstPassCount}</strong></div>
                        <div>2nd Pass Tags: <strong>{generateResult.hmiTagCount}</strong></div>
                        <div>Alarm Config Tags: <strong>{generateResult.alarmConfigCount}</strong></div>
                      </>
                    )}
                  </div>
                  <div className="stui-text-xs" style={{ marginTop: 'var(--spacing-sm)', borderTop: 'var(--border-width) solid var(--color-border-subtle)', paddingTop: 'var(--spacing-sm)' }}>
                    {[
                      { name: generateResult.fileNameA },
                      { name: generateResult.fileNameB },
                      ...(generateResult.fileC ? [{ name: generateResult.fileNameC }] : [])
                    ].map(({ name }) => (
                      <div key={name} className="stui-break-all">&#8595; {name}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

    </div>
  );
}
